import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { AmplifyAuthenticatorEnum } from 'enums';
import '@aws-amplify/ui-react/styles.css';
import logo from 'assets/images/logo.svg';
import './Login.scss';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { setSessionToken } from 'helpers/sessionToken';
import { getRoleHomePath } from 'constants/roles';
import UserService from 'services/UserService';
import { authActions } from 'store/slices/auth';
import { toast } from 'react-hot-toast';
import { translations } from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify/utils';
import { useQueryClient } from '@tanstack/react-query';
import { signOutUser } from 'helpers/signOut';
import {
  clearSessionEvicted,
  SESSION_EVICTED_EVENT,
  wasSessionEvicted,
} from 'helpers/sessionEviction';
import { setSentryUser } from 'config/sentry';
I18n.putVocabularies(translations);

const passwordCannotBeEmpty = 'Password cannot be empty';
const usernameCannotBeEmpty = 'Username cannot be empty';

I18n.putVocabulariesForLanguage('en', {
  'username is required to signIn': usernameCannotBeEmpty,
  'password is required to signIn': passwordCannotBeEmpty,
  'username is required to resetPassword': usernameCannotBeEmpty,
});

/**
 * @description Login component that handles user authentication using AWS Amplify.
 * Provides a login form with custom styling and redirects authenticated users
 * to their intended destination after successful authentication.
 *
 * @SADtrace SAD-003-1, SAD-003-1.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 * 08-18-2026 aleksandar dukleski: DLA-1469 explain a sign-out caused by a sign-in elsewhere
 *
 * @returns {JSX.Element} The rendered Login component
 */
const Login = () => {
  const { route } = useAuthenticator((context) => [context.route]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Read during render and clear on mount, rather than reading-and-clearing in one step: the
  // notice must survive re-renders of this page but not outlive the sign-out that caused it.
  const [sessionEvicted, setSessionEvicted] = useState(wasSessionEvicted);

  useEffect(() => {
    // Consume the marker that brought us here.
    clearSessionEvicted();

    // An eviction can also be detected while this page is already mounted — the calls it makes
    // itself return SESSION_EVICTED once displaced — and that path performs no reload, so there
    // is no second read. Consume it here too, or the marker outlives the sign-out and resurfaces
    // as a phantom notice on a later visit.
    const onSessionEvicted = () => {
      setSessionEvicted(true);
      clearSessionEvicted();
    };

    window.addEventListener(SESSION_EVICTED_EVENT, onSessionEvicted);

    return () =>
      window.removeEventListener(SESSION_EVICTED_EVENT, onSessionEvicted);
  }, []);

  useEffect(() => {
    /**
     * @description Updates the user information in the Redux store after successful authentication.
     * Fetches the current auth session, sets the JWT token in cookies, retrieves user data from the API,
     * and sets Sentry user context for error tracking.
     *
     * @SADtrace SAD-003-1, SAD-003-1.1, SAD-003-2.1
     *
     * @History
     * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
     * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
     * 07-02-2026 aleksandar dukleski: return the role so login lands on the explicit role home
     * 08-26-2026 aleksandar dukleski: DLA-1470 send the access token, not the id token
     *
     * @async
     *
     * @returns {Promise<string | undefined>} The authenticated user's role, or undefined when there is no active session.
     */
    const updateUser = async (): Promise<string | undefined> => {
      // Get current authenticated Cognito user
      const authSession = await fetchAuthSession();

      if (authSession.tokens) {
        setSessionToken(authSession.tokens);

        // Get the user and update the store
        const user = await UserService.getMe();
        dispatch(
          authActions.updateUser({
            user: user.data,
          }),
        );

        // Set Sentry user context
        setSentryUser(user.data);

        return user.data.role;
      }

      return undefined;
    };

    if (route === AmplifyAuthenticatorEnum.AUTHENTICATED) {
      updateUser()
        .then((role) => {
          // Land directly on the role's home route (e.g. /facilities for
          // Administration/Author). We navigate to the explicit path instead of
          // '/' + the router's index redirect: the router is rebuilt on role
          // change, and that second hop can strand the user on '/' after a
          // logout→login. We intentionally do NOT replay location.state.from —
          // see DLA-1502.
          navigate(getRoleHomePath(role), { replace: false });
        })
        .catch((e) => {
          toast.error(e.response?.data?.message || e.message);
          signOutUser(dispatch, queryClient);
        });
    }
  }, [route, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="login-page d-flex flex-column justify-content-center align-items-center">
      <div className="login-page__logo-wrapper">
        <img src={logo} alt="Logo" />
      </div>

      {/* Deliberately a persistent banner rather than a dialog: it stays readable while the user
          re-authenticates, which is exactly when "change your password" is actionable, and
          role="alert" announces it without taking focus from the sign-in form. */}
      {sessionEvicted && (
        <div className="login-page__session-alert" role="alert">
          <p className="login-page__session-alert-title">
            {t('session.evicted__title')}
          </p>
          <p className="login-page__session-alert-text">
            {t('session.evicted__text')}
          </p>
        </div>
      )}

      <Authenticator
        hideSignUp
        formFields={{
          signIn: {
            password: {
              labelHidden: true,
              autocomplete: 'new-password',
              //change default placeholder
              placeholder: 'Password',
            },
            username: {
              labelHidden: true,
              //change default placeholder
              placeholder: 'Username (email)',
            },
          },

          forgotPassword: {
            username: {
              labelHidden: true,
              //change default placeholder
              placeholder: 'Enter your username (email)',
            },
          },

          setupTotp: {
            QR: {
              totpIssuer:
                // v8 ignore start -- WAIVER(ENV): build-environment gate on import.meta.env — the production arm exists only in production builds and cannot execute under the test environment
                import.meta.env.VITE_APP_ENV !== 'production'
                  ? `Drug Library (${import.meta.env.VITE_APP_ENV})`
                  : '',
              // v8 ignore stop -- WAIVER(ENV)
            },
          },
        }}
      ></Authenticator>
    </div>
  );
};

export default Login;
